import { useEffect, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";
import { FieldRow, FieldCheckboxRow, blurOnEnter } from "./Field";

/**
 * Conditional visibility section — `conditionalVisibility`.
 *
 * Top-level "Show conditionally" checkbox toggles between
 *   - off: `conditionalVisibility = null`
 *   - on:  `{ field: '', operator: 'equals', value: '' }` (default seed)
 *
 * When on, three sub-controls render:
 *   - Control field — `<select>` of all OTHER fields (no self-reference)
 *   - Operator     — `<select>` of all 7 operators
 *   - Value        — text input, hidden for `isEmpty` / `isNotEmpty`
 *     (those operators don't take a comparison value)
 *
 * Each sub-control writes the WHOLE `conditionalVisibility` object back
 * to the store via `updateField`. The two selects commit on change; the
 * value input uses the same blur/Enter commit pattern as the other
 * sections (DisplaySection / BehaviorSection).
 *
 * Restored from the legacy `FieldEditor.tsx` (removed in commit 83cb18c)
 * as part of WU-4. A Tier-2 publish-time validator that warns on broken
 * field references (e.g. controller field renamed/deleted) is filed as
 * a follow-up — no validator currently exists for this predicate.
 */

type ConditionalVisibility = NonNullable<FieldContent["conditionalVisibility"]>;
type Operator = ConditionalVisibility["operator"];

const OPERATORS: ReadonlyArray<{ value: Operator; label: string }> = [
  { value: "equals", label: "equals" },
  { value: "notEquals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "greaterThan", label: "greater than" },
  { value: "lessThan", label: "less than" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];

const VALUELESS_OPERATORS: ReadonlySet<Operator> = new Set(["isEmpty", "isNotEmpty"]);

const DEFAULT_CV: ConditionalVisibility = { field: "", operator: "equals", value: "" };

export function ConditionalVisibilitySection() {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;
  const cv = field?.conditionalVisibility ?? null;

  const [valueDraft, setValueDraft] = useState(cv?.value ?? "");

  useEffect(() => {
    setValueDraft(cv?.value ?? "");
  }, [field?.fieldDefinitionId, cv?.value]);

  if (!field || !content) return null;

  function toggleEnabled(next: boolean) {
    if (!field) return;
    updateField(field.name, {
      conditionalVisibility: next ? { ...DEFAULT_CV } : null,
    });
  }

  function commitField(nextField: string) {
    if (!field || !cv) return;
    updateField(field.name, {
      conditionalVisibility: { ...cv, field: nextField },
    });
  }

  function commitOperator(nextOp: Operator) {
    if (!field || !cv) return;
    // When switching to a valueless operator, drop the `value` key so
    // the persisted shape matches the type signature
    // (`value?: string` — omitted, not empty-string).
    if (VALUELESS_OPERATORS.has(nextOp)) {
      updateField(field.name, {
        conditionalVisibility: { field: cv.field, operator: nextOp },
      });
    } else {
      updateField(field.name, {
        conditionalVisibility: { ...cv, operator: nextOp, value: cv.value ?? "" },
      });
    }
  }

  function commitValue() {
    if (!field || !cv) return;
    if (valueDraft === (cv.value ?? "")) return;
    updateField(field.name, {
      conditionalVisibility: { ...cv, value: valueDraft },
    });
  }

  const otherFields = content.fields.filter((f) => f.name !== field.name);
  const showValueInput = cv !== null && !VALUELESS_OPERATORS.has(cv.operator);

  return (
    <section className="field-section" aria-label="Conditional Visibility">
      <h3 className="field-section__title">Conditional Visibility</h3>

      <FieldCheckboxRow
        id="cv-enabled"
        checked={cv !== null}
        label="Show conditionally"
        hint="Hide this field until the predicate below evaluates true."
        onChange={toggleEnabled}
      />

      {cv !== null && (
        <>
          <FieldRow
            htmlFor="cv-field"
            label="Control field"
            hint="The field whose value drives visibility."
          >
            <select
              id="cv-field"
              className="field-section__input"
              value={cv.field}
              onChange={(e) => commitField(e.target.value)}
            >
              <option value="">— select a field —</option>
              {/* Phase-1: f.name === f.internalName (snapshot-builder sets both). If they ever diverge (Phase 2), key on f.internalName. */}
              {otherFields.map((f) => (
                <option key={f.fieldDefinitionId} value={f.name}>
                  {f.label || f.name}
                </option>
              ))}
            </select>
          </FieldRow>

          <FieldRow htmlFor="cv-operator" label="Operator">
            <select
              id="cv-operator"
              className="field-section__input"
              value={cv.operator}
              onChange={(e) => commitOperator(e.target.value as Operator)}
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </FieldRow>

          {showValueInput && (
            <FieldRow
              htmlFor="cv-value"
              label="Value"
              hint="The value the operator compares against."
            >
              <input
                id="cv-value"
                type="text"
                className="field-section__input"
                value={valueDraft}
                onChange={(e) => setValueDraft(e.target.value)}
                onBlur={commitValue}
                onKeyDown={blurOnEnter}
              />
            </FieldRow>
          )}
        </>
      )}
    </section>
  );
}
